<template>
  <div class="abtest-details-component">
    <div class="dates-and-id">
      <div v-if="startDate || endDate" class="dates">
        <vd-custom-icon name="timer-icon" big />
        <el-tooltip v-if="startDate" :content="timezoneHint" placement="top">
          <span>
            <span v-if="!endDate">Starts on</span>
            {{ startDate }}
          </span>
        </el-tooltip>
        <vd-custom-icon v-if="startDate && endDate" name="next-arrow-icon" />
        <el-tooltip v-if="endDate" :content="timezoneHint" placement="top">
          <div>
            <span>
              <span v-if="!startDate">Ends on</span>
              {{ endDate }}
            </span>
          </div>
        </el-tooltip>
      </div>
      <div v-else class="dates">
        <vd-custom-icon name="timer-icon" big />
        <span>Endless</span>
      </div>
      <div class="abtest-id">
        <span>A/B Test ID:&nbsp;</span>
        <span class="technical-name">{{ abtest.id }}</span>
      </div>
    </div>
    <div v-if="abtest.description && abtest.description !== ''" class="description-quote">
      <p>{{ abtest.description }}</p>
    </div>
    <VdCard
      v-for="cohort in abtest.cohorts"
      :key="cohort.id"
      class="cohorts-list-item"
      size="small"
      expandable
      expanded
    >
      <div class="cohort">
        <div>
          <h4 class="cohort-name">
            {{ cohort.name }}
          </h4>
          <div class="cohort-id">
            Cohort ID:
            <span class="technical">{{ cohort.id }}</span>
          </div>
        </div>
        <div class="tail">
          <vd-button
            v-if="showTestButton"
            type="secondary"
            skin="blue"
            size="small"
            class="test-button"
            @click="() => onAttachTester(cohort.id)"
          >
            <VdIcon store="fas" name="mobile-screen" size="small" class="mobile-icon" />
            Test
          </vd-button>
          <div class="allocation-rate">
            <vd-custom-icon name="multiple-users-icon" big />
            <span>{{ cohort.allocationRate }}%</span>
          </div>
        </div>
      </div>
      <template #expanded-content>
        <CohortDetails
          :cohort="cohort"
          :shops="shopsToDisplay"
          :ab-test-type="abTestType ?? 'class'"
          :is-dynamic-ab="abtest.isDynamic"
          :dynamic-segments="abtest.dynamicSegments ?? []"
          :ab-test-id="abtest.id"
          :ab-test-status="abtest.state"
          :offers-by-id="offersById"
          :campaign-names-by-id="campaignNamesById"
        />
      </template>
    </VdCard>
  </div>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdCard } from "@voodoo.io/voodoo-ui";
import { format, parseISO } from "date-fns";
import { PropType, computed } from "vue";

import CohortDetails from "@/components/CohortDetails/CohortDetails.vue";
import { AbtestFragment } from "@/graphql";
import { TreatmentOfferSummary } from "@/modules/Offers/hooks/useTreatmentOffersData";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";
import { formatAbTestType } from "@/utils/formatAbTestType";

interface DynamicAbtest {
  isDynamic: boolean;
  dynamicSegments: { id: string; name: string }[];
}

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  abtest: {
    type: Object as PropType<AbtestFragment & DynamicAbtest>,
    required: true,
  },

  /**
   * True if the A/B Test is archived
   */
  archived: {
    type: Boolean,
    default: false,
  },

  /**
   * True if the button "test" should be displayed in each cohort
   */
  showTestButton: {
    type: Boolean,
    default: false,
  },

  // Offer/campaign lookups fetched once at page level (useTreatmentOffersData), forwarded to CohortDetails.
  offersById: {
    type: Object as PropType<Record<string, TreatmentOfferSummary>>,
    default: () => ({}),
  },
  campaignNamesById: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
});

const emit = defineEmits(["attach-tester"]);

let startDate: string;
if (props.abtest.beginsAt) {
  startDate = format(parseISO(props.abtest.beginsAt), "eee dd MMM yy (HH:mm)");
}

let endDate: string;
if (props.abtest.endsAt) {
  endDate = format(parseISO(props.abtest.endsAt), "eee dd MMM yy (HH:mm)");
}

const timezoneHint = `Time shown in your local timezone (${Intl.DateTimeFormat().resolvedOptions().timeZone})`;

const onAttachTester = (cohortId: string) => {
  emit("attach-tester", cohortId);
};

const abTestType = computed(() => formatAbTestType(props.abtest.treatmentTypes ?? []));
const { shopsToDisplay } = useShopsManager(props.gameId, props.versionId);
</script>

<style lang="scss">
.abtest-details-component {
  .test-button {
    margin-right: size("M");

    .mobile-icon {
      margin-right: size("XS");
    }
  }

  .dates-and-id {
    display: flex;
    justify-content: space-between;

    .technical-name {
      @extend %technical-name;
    }

    .dates {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;

      .custom-icon-component {
        margin: 0 size("S");
      }
    }
  }

  .description-quote {
    margin: size("M") 0 size("M") size("ML");
    white-space: pre;
  }

  .cohorts-list-item {
    &.vd-card {
      background-color: var(--voodootune-background-color);
    }

    margin: size("ML") 0;

    .el-card__body {
      padding: size("L") size("ML");
    }

    .vd-card__content {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
    }

    .cohort {
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .tail {
        display: flex;
        align-items: center;

        .allocation-rate {
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          gap: size("XS");
          font-weight: 600;
        }
      }
    }

    h4.cohort-name {
      margin-bottom: size("S");
      font-weight: 600;
    }

    .cohort-id {
      font-size: size("M");
    }

    .technical {
      @extend %technical-name;
      color: $slate;
    }
  }
}
</style>
